import { useEffect, useRef } from "react";
import { useThemeSubscription } from "@/hooks/useTheme";

// Persistent WebGL-free particle canvas — drifts upward with depth-based
// parallax reacting to scrollY. Creates the "traveling through a semiconductor
// factory" ambient atmosphere across the entire page.
export default function ParticleField() {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const scrollRef = useRef(0);
  const particlesRef = useRef([]);
  const dimsRef = useRef({ w: 0, h: 0, dpr: 1 });
  const theme = useThemeSubscription();
  const isDark = theme === "dark";

  useEffect(() => {
    if (typeof window === "undefined") return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = window.innerWidth;
      const h = window.innerHeight;
      dimsRef.current = { w, h, dpr };
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    // Denser near the viewport, some near, some far (parallax)
    const NUM = reduce ? 40 : 110;
    const particles = [];
    for (let i = 0; i < NUM; i++) {
      const depth = Math.random(); // 0 far, 1 near
      const isTealAccent = Math.random() < 0.22;
      particles.push({
        x: Math.random() * dimsRef.current.w,
        y: Math.random() * dimsRef.current.h,
        baseY: Math.random() * dimsRef.current.h * 4,
        r: 0.4 + depth * 1.7,
        vx: (Math.random() - 0.5) * (0.06 + depth * 0.15),
        vy: -0.05 - depth * 0.35,
        alpha: isDark ? 0.18 + depth * 0.55 : 0.12 + depth * 0.32,
        depth,
        hue: isDark
          ? (isTealAccent ? 205 : 187) // bright cyan-teal on dark
          : (isTealAccent ? 195 : 226), // dark navy-teal on beige
        sat: isDark ? 100 : (isTealAccent ? 70 : 52),
        light: isDark ? (isTealAccent ? 65 : 66) : (isTealAccent ? 30 : 14),
        pulse: Math.random() * Math.PI * 2,
      });
    }
    particlesRef.current = particles;

    const onScroll = () => {
      scrollRef.current = window.scrollY || window.pageYOffset || 0;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", resize);

    const draw = () => {
      const { w, h } = dimsRef.current;
      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = isDark ? "lighter" : "multiply";

      const scrollY = scrollRef.current;

      for (const p of particlesRef.current) {
        // Update position
        p.x += p.vx;
        p.y += p.vy;
        p.pulse += 0.03;

        if (p.x < -12) p.x = w + 12;
        else if (p.x > w + 12) p.x = -12;

        // Parallax reactive to scrollY
        const parallax = scrollY * (0.03 + p.depth * 0.32);
        const yWorld = p.y - parallax;
        const wrapped = ((yWorld % (h + 220)) + (h + 220)) % (h + 220) - 110;

        const alphaFlicker = p.alpha * (0.75 + Math.sin(p.pulse) * 0.25);

        // Halo
        const R = p.r * (5.5 + p.depth * 4);
        const g = ctx.createRadialGradient(p.x, wrapped, 0, p.x, wrapped, R);
        g.addColorStop(0, `hsla(${p.hue}, ${p.sat}%, ${p.light}%, ${alphaFlicker * 0.6})`);
        g.addColorStop(0.4, `hsla(${p.hue}, ${p.sat}%, ${p.light}%, ${alphaFlicker * 0.16})`);
        g.addColorStop(1, `hsla(${p.hue}, ${p.sat}%, ${p.light}%, 0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(p.x, wrapped, R, 0, Math.PI * 2);
        ctx.fill();

        // Core dot
        ctx.fillStyle = `hsla(${p.hue}, ${p.sat}%, ${p.light}%, ${Math.min(1, alphaFlicker + 0.15)})`;
        ctx.beginPath();
        ctx.arc(p.x, wrapped, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      rafRef.current = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", resize);
    };
  }, [isDark]);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none fixed inset-0"
      style={{ zIndex: 1, mixBlendMode: isDark ? "screen" : "multiply" }}
      aria-hidden="true"
      data-testid="particle-field"
    />
  );
}
